import { ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TemplateConfig, GoogleFont } from "@/types/email-templates";

interface FontSettingsProps {
  config: TemplateConfig;
  isOpen: boolean;
  onToggle: () => void;
  onFontChange: (key: keyof TemplateConfig["fonts"], value: string | number) => void;
  onGoogleFontChange: (value: string) => void;
  googleFonts: GoogleFont[];
}

const FontSettings = ({
  config,
  isOpen,
  onToggle,
  onFontChange,
  onGoogleFontChange,
  googleFonts,
}: FontSettingsProps) => {
  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="text-lg flex items-center gap-2">
              Fonts
              <ChevronDown
                className="ml-auto h-4 w-4 transition-transform"
                style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="googleFont" className="text-sm">
                Font
              </Label>
              <Select value={config.fonts?.googleFont || "Poppins"} onValueChange={onGoogleFontChange}>
                <SelectTrigger id="googleFont" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {googleFonts.map((font) => (
                    <SelectItem key={font} value={font}>
                      {font}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Select from available Google Fonts</p>
            </div>

            <div>
              <Label htmlFor="headingSize" className="text-sm">
                Heading Size (px)
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="headingSize"
                  type="number"
                  min="16"
                  max="64"
                  value={config.fonts?.headingSize || 32}
                  onChange={(e) => onFontChange("headingSize", parseInt(e.target.value))}
                  className="text-sm flex-1"
                />
                <div className="flex items-center px-3 bg-muted rounded text-sm font-medium">
                  {config.fonts?.headingSize || 32}px
                </div>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default FontSettings;
